// frame: CJ0w0 @ ui/dogdisk-user.pen
// spec: docs/ui/user-page-flows.md §2.4 CJ0w0 页面-回收站
//
// 数据源:v_file_info_recycled(file_storage.md §3.2;status=recycled,
// 30 天后自动 → deleted)。
// 恢复:POST /gapi/user/trash/restore-batch(批量;file-management.md §3)。
// 清空:POST /gapi/user/trash/purge-batch(物理删除,100/批;UI 弹 xQSG7 二次确认后分批清)。

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, formatRelativeTime, getSupabase } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen, TopBar } from '../components/mobile-screen'

type TrashRow = {
  id: string
  name: string
  type: string
  recycled_at: string
  /** 文件夹子树内仍 recycled 的后代数(v_file_info_recycled.child_count);用于"含 N 项" */
  child_count?: number
  /** server 端不一定有;UI 用于显示"剩余 N 天"提示 */
  expires_at?: string | null
}

const TRASH_DAYS = 30

function daysLeft(recycledAt: string): number {
  const recycledMs = new Date(recycledAt).getTime()
  if (Number.isNaN(recycledMs)) return TRASH_DAYS
  const elapsed = (Date.now() - recycledMs) / 86_400_000
  return Math.max(0, Math.round(TRASH_DAYS - elapsed))
}

function useTrash() {
  return useQuery({
    queryKey: ['trash', 'files'] as const,
    queryFn: async (): Promise<TrashRow[]> => {
      const sb = getSupabase()
      const { data, error } = await sb
        .from('v_file_info_recycled')
        .select('id, name, type, recycled_at, child_count')
        .order('recycled_at', { ascending: false })
        .limit(200)
      if (error) throw error
      return (data as TrashRow[]) ?? []
    },
    // 删除/恢复发生在别处(file-actions / batch-action-bar 等),不会 invalidate
    // ['trash','files'];全局 staleTime 30s 又会把陈旧缓存当新鲜。回收站每次进入
    // 都强制重拉,保证「点进来即看到最新列表」,不依赖手动刷新。
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function TrashPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: rows = [], isLoading, error } = useTrash()
  const [confirmClear, setConfirmClear] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const restoreMut = useMutation({
    mutationFn: (id: string) => apiClient.post(`/gapi/user/trash/${id}/restore`, {}),
    onMutate: (id) => setBusyId(id),
    onSettled: () => {
      setBusyId(null)
      void qc.invalidateQueries({ queryKey: ['trash', 'files'] })
      void qc.invalidateQueries({ queryKey: ['file', 'list'] })
    },
  })

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <div data-testid="CJ0w0" className="flex flex-1 flex-col">
          <TopBar
            title="回收站"
            fallback="/files"
            right={
              rows.length > 0 ? (
                <button
                  type="button"
                  data-testid="1gV4V"
                  onClick={() => setConfirmClear(true)}
                  className="text-[12px] font-medium text-[#FF6B6B]"
                >
                  清空
                </button>
              ) : null
            }
          />

          <div className="mx-6 mt-2 rounded-xl bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
            回收站文件将在 {TRASH_DAYS} 天后自动删除
          </div>

          <div className="flex-1 px-6 pb-24 pt-3">
            {isLoading ? (
              <SkeletonRows />
            ) : error ? (
              <p className="py-12 text-center text-[13px] text-[#FF6B6B]">
                加载失败,请稍后重试
              </p>
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-[#9CA3AF]">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M6 6l1 14a2 2 0 002 2h6a2 2 0 002-2l1-14" />
                </svg>
                <p>回收站为空</p>
                <button
                  type="button"
                  onClick={() => navigate('/files')}
                  className="mt-2 text-[12px] text-[#1971ca]"
                >
                  返回文件列表
                </button>
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {rows.map((f, i) => {
                  // node_id 来自 .pen restoreBtn1/2/3(WQklF/Z5zmQ/OKvn1)
                  const restoreId =
                    i === 0 ? 'WQklF' : i === 1 ? 'Z5zmQ' : i === 2 ? 'OKvn1' : `restore-${i}`
                  return (
                    <li
                      key={f.id}
                      className="flex items-center gap-3 rounded-xl bg-white px-3 py-3"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
                        {f.type === 'folder' ? '📁' : '📄'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[14px] font-medium text-[#1A1A1A]">
                          {f.name}
                        </p>
                        <TrashItemMeta
                          type={f.type}
                          childCount={f.child_count}
                          recycledAt={f.recycled_at}
                        />
                      </div>
                      <button
                        type="button"
                        data-testid={restoreId}
                        disabled={busyId === f.id}
                        onClick={() => restoreMut.mutate(f.id)}
                        className="rounded-md bg-emerald-50 px-3 py-1.5 text-[12px] font-medium text-emerald-700 active:bg-emerald-100 disabled:opacity-50"
                      >
                        {busyId === f.id ? '恢复中…' : '恢复'}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          {confirmClear && (
            <ConfirmClearDialog
              count={rows.length}
              onCancel={() => setConfirmClear(false)}
              onConfirm={async () => {
                setConfirmClear(false)
                const ids = rows.map((r) => r.id)
                // purge-batch 单批上限 100,分批清。
                for (let i = 0; i < ids.length; i += 100) {
                  const batch = ids.slice(i, i + 100)
                  await apiClient
                    .post('/gapi/user/trash/purge-batch', { file_ids: batch })
                    .catch(() => null)
                }
                void qc.invalidateQueries({ queryKey: ['trash', 'files'] })
                void qc.invalidateQueries({ queryKey: ['file', 'list'] })
              }}
            />
          )}
        </div>
      </MobileScreen>
    </AppLayout>
  )
}

// 回收站条目副标题:文件夹有子项时前缀"含 N 项",再接删除时间 + 剩余天数。
// 删文件夹只显示顶层一条(v_file_info_recycled 已折叠),child_count = 子树内 recycled 后代数。
export function TrashItemMeta({
  type,
  childCount,
  recycledAt,
}: {
  type: string
  childCount?: number | undefined
  recycledAt: string
}) {
  const left = daysLeft(recycledAt)
  const showCount = type === 'folder' && (childCount ?? 0) > 0
  return (
    <p className="text-[11px] text-[#9CA3AF]">
      {showCount ? `含 ${childCount} 项 · ` : ''}删除于 {formatRelativeTime(recycledAt)} · 剩余{' '}
      {left} 天
    </p>
  )
}

function ConfirmClearDialog({
  count,
  onCancel,
  onConfirm,
}: {
  count: number
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="xQSG7"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-8"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-[320px] rounded-2xl bg-white p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-center text-[15px] font-semibold text-[#1A1A1A]">清空回收站</h3>
        <p className="mt-2 text-center text-[12px] text-[#9CA3AF]">
          将永久删除 {count} 个文件,此操作不可恢复
        </p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-11 flex-1 rounded-lg bg-[#F1F2F4] text-[14px] font-semibold text-[#1A1A1A]"
          >
            取消
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="h-11 flex-1 rounded-lg bg-[#FF6B6B] text-[14px] font-semibold text-white"
          >
            确认清空
          </button>
        </div>
      </div>
    </div>
  )
}

function SkeletonRows() {
  return (
    <ul className="flex flex-col gap-2">
      {[1, 2, 3].map((i) => (
        <li key={i} className="flex items-center gap-3 rounded-xl bg-white px-3 py-3">
          <span className="skeleton h-10 w-10 rounded-lg" />
          <div className="flex-1 space-y-2">
            <span className="skeleton block h-3 w-3/5 rounded" />
            <span className="skeleton block h-2 w-2/5 rounded" />
          </div>
        </li>
      ))}
    </ul>
  )
}
